import React, { type ReactElement, useCallback, useEffect, useMemo } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, type SubmitHandler, useForm } from 'react-hook-form';
import Controls from './controls';
import PageSize from './page-size';
import { type PaginationFormValues, getPaginationFormSchema } from './pagination-form.schema';
import Summary from './summary';
import styles from './index.module.scss';

type PaginationFormProps = {
  currentPage: number;
  itemsPerPage: number;
  totalItems: number;
  onSubmit: SubmitHandler<PaginationFormValues>;
};

export default function PaginationForm({
  currentPage,
  itemsPerPage,
  totalItems,
  onSubmit
}: PaginationFormProps): ReactElement {
  const defaultValues: PaginationFormValues = useMemo(
    () => ({
      pageSize: itemsPerPage,
      page: currentPage,
      recordOfTotalItems: totalItems
    }),
    [currentPage, itemsPerPage, totalItems]
  );

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const methods = useForm({
    mode: 'onTouched',
    resolver: zodResolver(getPaginationFormSchema(totalPages)),
    defaultValues
  });
  const { reset, handleSubmit, watch } = methods;

  const triggerFormSubmission = useCallback(() => {
    handleSubmit(onSubmit)();
  }, [handleSubmit, onSubmit]);

  /**
   * Prevent outer query params/filters from getting out of sync with form state
   * after initial load. When totalItems changes we can consider the form's
   * page size and number to no longer be reliable. We should perform a hard
   * reset of the form so that we can load from the updated props:
   */
  const recordOfTotalItems = watch('recordOfTotalItems');

  useEffect(() => {
    if (totalItems !== recordOfTotalItems) {
      reset(defaultValues);
    }
  }, [totalItems, recordOfTotalItems, reset, currentPage, itemsPerPage, defaultValues]);

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} className={styles.paginationForm}>
        <PageSize triggerFormSubmission={triggerFormSubmission} />
        <Summary currentPage={currentPage} itemsPerPage={itemsPerPage} totalItems={totalItems} />
        {totalItems > 0 && (
          <Controls
            triggerFormSubmission={triggerFormSubmission}
            currentPage={currentPage}
            totalPages={totalPages}
          />
        )}
      </form>
    </FormProvider>
  );
}
